import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import type {
  GetMasterPayrollListV2ResponseInterface,
  GetPayrollAttributesResponseInterface,
  GetMasterPayrollValidateDetailResponseInterface,
  GetPayrollDetailByUserIdServicesInterface,
  MasterRateBulkUploadResponseInterface,
  PayrollQueryParamsInterface,
} from 'features/Enterprise/Payroll/types'

const MULTIPART_FORM_DATA_CONTENT_TYPE = 'multipart/form-data'

export const getMasterPayrollListV2 = (params: {
  cursor: string
  location_id: string
  month: string
  page_size: number
  payroll_type?: string
  staff_id?: string
  staff_name?: string
  publish_status?: string
  year: string
}) =>
  axiosInstanceApiGateway.Get<GetMasterPayrollListV2ResponseInterface, true>({
    url: 'staff-management/internal/:version/payroll',
    params,
    version: 'v2',
    convertRequest: true,
  })

export const getDetailPayrollByUserIdServices = (
  params: PayrollQueryParamsInterface & { staffId: string | number },
) =>
  axiosInstanceApiGateway.Get<GetPayrollDetailByUserIdServicesInterface, true>({
    url: 'staff-management/internal/:version/payroll/detail',
    params,
    version: 'v1',
    convertRequest: true,
  })

export const getMasterPayrollPayslipPdf = (params: { payrollId: string | number }) =>
  axiosInstanceApiGateway.Get<{ data: string }, true>({
    url: 'staff-management/internal/:version/payroll/pdf',
    params,
    version: 'v2',
    convertRequest: true,
  })

export const getMasterPayrollValidateDetail = (id: string | number) =>
  axiosInstanceApiGateway.Get<GetMasterPayrollValidateDetailResponseInterface, true>({
    url: 'staff-management/internal/:version/payroll/:id',
    version: 'v2',
    convertRequest: true,
    urlParams: { id },
  })

export type PostPayrollValidatePayloadType = {
  data: {
    detail_kehadiran: {
      role: string
      ho: string
      ho2: string
      days: string
      ot_4: string
      ot_2_4: string
      onleave: string
      half_day: string
      ontime: string
      late_lt_15: string
      late_gt_15: string
    }[]
    pendapatan_lainnya: { name: string; value: number }[]
    potongan: { name: string; value: number }[]
  }
  id: string
}

export const postPayrollValidate = (id: string | number, payload: PostPayrollValidatePayloadType) =>
  axiosInstanceApiGateway.Post<
    { message: string; error: { status: boolean; code: number; message: string } },
    true
  >({
    url: 'staff-management/internal/:version/payroll/:id/validate',
    version: 'v2',
    urlParams: { id },
    data: payload,
    convertRequest: true,
  })

export type PostPublishPayrollByFilterPayloadType = {
  location_id: string
  month: string
  payroll_type?: string
  year: string
}

export const postPublishPayrollByFilter = (payload: PostPublishPayrollByFilterPayloadType) =>
  axiosInstanceApiGateway.Post<
    { message: string; error: { status: boolean; code: number; message: string } },
    true
  >({
    url: 'staff-management/internal/:version/payroll/publish',
    version: 'v2',
    data: payload,
    convertRequest: true,
  })

export type PostValidatePayrollByFilterPayloadType = {
  location_id: string
  month: string
  payroll_type?: string
  year: string
}

export const postValidatePayrollByFilter = (payload: PostValidatePayrollByFilterPayloadType) =>
  axiosInstanceApiGateway.Post<
    { message: string; error: { status: boolean; code: number; message: string } },
    true
  >({
    url: 'staff-management/internal/:version/payroll/validate-by-filter',
    version: 'v2',
    data: payload,
    convertRequest: true,
  })

export type PostPublishPayrollByIdsPayloadType = {
  ids: string[]
}

export const postPublishPayrollByIds = (payload: PostPublishPayrollByIdsPayloadType) =>
  axiosInstanceApiGateway.Post<
    { message: string; error: { status: boolean; code: number; message: string } },
    true
  >({
    url: 'staff-management/internal/:version/payroll/publish/by-ids',
    version: 'v2',
    data: payload,
    convertRequest: true,
  })

export const getMasterPayrollBulkUploadTemplate = (params: {
  payoutType: string
  staffStatus: string
}) =>
  axiosInstanceApiGateway.Get<{ data: { file_url: string } }, true>({
    url: 'staff-management/internal/:version/payroll/template',
    params,
    version: 'v1',
    convertRequest: true,
  })

export const postMasterPayrollBulkUpload = (data: unknown) =>
  axiosInstanceApiGateway.Post({
    url: 'staff-management/internal/:version/payroll/import',
    data,
    version: 'v1',
    contentType: MULTIPART_FORM_DATA_CONTENT_TYPE,
  })

export const getPayrollAttributes = (params: { payout_type: string }) =>
  axiosInstanceApiGateway.Get<GetPayrollAttributesResponseInterface>({
    url: 'staff-management/internal/:version/payroll/attribute',
    params,
    version: 'v1',
    convertRequest: true,
  })

export const postUpsertPayrollAttributes = (data: {
  payout_type: string
  effective_date: string
  detail: {
    category: string
    label: string
    owner: string
    sort_order: number
    is_visible: boolean
  }[]
}) =>
  axiosInstanceApiGateway.Post({
    url: 'staff-management/internal/:version/payroll/attribute',
    data,
    version: 'v1',
    convertRequest: true,
  })

export const getPayrollAttributeExportTemplate = (params: {
  end_date?: string
  location_id?: string
  owner: string
  payroll_type?: string
  start_date?: string
}) =>
  axiosInstanceApiGateway.Get<{ data: { file_url: string } }, true>({
    url: 'staff-management/internal/:version/payroll/attribute/export',
    params,
    version: 'v2',
    convertRequest: true,
  })

export const getPayrollValidationExportTemplate = (params: {
  end_date?: string
  location_id?: string
  owner: string
  payroll_type: string
  start_date?: string
}) =>
  axiosInstanceApiGateway.Get<{ data: { file_url: string } }, true>({
    url: 'staff-management/internal/:version/payroll/validation/export',
    params,
    version: 'v2',
    convertRequest: true,
  })

export const postPayrollAttributeBulkUpload = (data: FormData) =>
  axiosInstanceApiGateway.Post<{ data: MasterRateBulkUploadResponseInterface }, true>({
    url: 'staff-management/internal/:version/payroll/attribute/bulk-upload',
    data,
    version: 'v2',
    convertResponse: true,
    config: {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': MULTIPART_FORM_DATA_CONTENT_TYPE,
      },
    },
  })

export const postPayrollValidationBulkUpload = (data: FormData) =>
  axiosInstanceApiGateway.Post<{ data: MasterRateBulkUploadResponseInterface }, true>({
    url: 'staff-management/internal/:version/payroll/validation/bulk-upload',
    data,
    version: 'v2',
    convertResponse: true,
    config: {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': MULTIPART_FORM_DATA_CONTENT_TYPE,
      },
    },
  })
